import { SAME_SITE_STRICT } from '@constants/cookies';
import { IS_DEV_NODE_ENV } from '@constants/node-envs';
import Cookies from 'js-cookie';

/**
 * Where the user's manual light/dark choice lives.
 *
 * It used to live in a cookie alone, which loses the choice on Android (MOB-790).
 * The Android WebView keeps cookie writes in memory and only flushes them to disk
 * periodically, so killing the app discards the write and the next launch reads the
 * PREVIOUS value — measured on staging: a toggle to light read back as `false`
 * in-session and came back as `true` after a restart. iOS never showed it because
 * WKWebView persists its cookie store eagerly.
 *
 * So `localStorage` is the source of truth and the cookie is kept in sync, which is
 * the shape the auth tokens already use (`use-token-management`). The one difference
 * matters: auth falls back to storage only when the cookie is MISSING, and here the
 * cookie is present with a stale value — a missing-only fallback would read the
 * stale cookie and never look at storage. Storage therefore wins outright, and the
 * cookie is only consulted when storage has nothing to say.
 *
 * The cookie is still written for two reasons: `index.html` reads it before React
 * boots to paint the right theme, and it is what a user who chose their theme before
 * this change is still carrying. Both stores stay in lock-step on every toggle.
 *
 * One consequence worth being explicit about, because it is a real behaviour change:
 * the cookie is scoped to `.xceed.me` while localStorage is per ORIGIN. The choice
 * used to be shared across every xceed property; now, for anyone who has toggled in
 * this app, the per-origin value wins over the shared one. Within pro that reads as
 * an improvement (staging stops deciding production's theme), but a theme set on
 * another xceed property no longer follows the user here.
 */
// Same literal deliberately: one names a cookie, the other a storage key, and they
// are kept identical so a value written by either is recognisable as the same
// preference when debugging. The agreement test in `tests/` pins the storage one
// against `index.html`, which cannot import from here.
export const DARK_MODE_COOKIE = 'xceed-dark-mode';
export const DARK_MODE_STORAGE_KEY = 'xceed-dark-mode';

const cookieDomain = IS_DEV_NODE_ENV ? 'localhost' : '.xceed.me';

// Every access is guarded: a WebView with storage disabled throws on access rather
// than returning null, and a theme preference is never worth breaking a launch over.
const readStorage = (): string | null => {
  try {
    return localStorage.getItem(DARK_MODE_STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeStorage = (value: string): void => {
  try {
    localStorage.setItem(DARK_MODE_STORAGE_KEY, value);
  } catch {
    // The cookie write below still stands; this only costs durability on Android.
  }
};

/**
 * The stored manual choice, or `null` when the user has never chosen — which is what
 * tells the caller to fall back to the native launch theme or the OS preference.
 */
export const readDarkModeChoice = (): boolean | null => {
  const stored = readStorage();
  if (stored !== null) {
    return stored === 'true';
  }

  const cookie = Cookies.get(DARK_MODE_COOKIE);
  if (cookie !== undefined) {
    return cookie === 'true';
  }

  return null;
};

const writeCookie = (value: string): void => {
  Cookies.set(DARK_MODE_COOKIE, value, {
    domain: cookieDomain,
    path: '/',
    sameSite: SAME_SITE_STRICT,
    secure: true,
    expires: 3650,
  });
};

/** Writes both stores, so neither can be read as authoritative-but-stale. */
export const persistDarkModeChoice = (isDark: boolean): void => {
  const value = isDark.toString();
  writeStorage(value);
  writeCookie(value);
};

/**
 * Brings the two stores back into agreement, in whichever direction is needed.
 *
 * Cookie → storage covers users who chose their theme before this existed: without it
 * their preference stays cookie-only and keeps getting lost on Android until they
 * happen to toggle again.
 *
 * Storage → cookie covers the state MOB-790 leaves behind: storage kept the choice,
 * the cookie reverted to the pre-toggle value. Reads already ignore that cookie, so
 * leaving it is not neutral — it is a value we deliberately distrust, sitting where
 * anything else would trust it. Two ways it bites: another `.xceed.me` property reads
 * it, or a storage eviction clears storage without clearing cookies and the original
 * bug comes back with the user's old theme.
 *
 * The honest limitation: with no timestamp this cannot tell "stale Android cookie"
 * from "a legitimate choice made on another xceed property", and it overwrites the
 * second. That is the same call the read order already makes — this only stops the
 * two stores from holding contradictory answers.
 *
 * Kept separate from `readDarkModeChoice` so that stays a pure read: it is called
 * from a render body, and a write there would fire on every render.
 */
export const reconcileDarkModeStores = (): void => {
  const stored = readStorage();
  const cookie = Cookies.get(DARK_MODE_COOKIE);

  if (stored === null) {
    if (cookie !== undefined) {
      writeStorage(cookie);
    }
    return;
  }

  if (cookie !== stored) {
    writeCookie(stored);
  }
};
